"use client";

import Image from "next/image";
import React, { useCallback, useMemo } from "react";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { RenewalPage } from "@/config/app-constant";
import { APPLICATION } from "@/constants";
import { resolveMagnoliaAssetUrl } from "@/utils/cms-image-resolver";

const DEFAULT_BTA_CONTENT = {
  btaTitle1: "Vote for Staysure at the",
  btaTitle2: "British Travel Awards",
  btaPara1:
    "We've been chosen for the BTA Award for 9 years in a row by our loyal customers like you.",
  btaPara2:
    "If we've helped you feel safe and confident on your journey, we'd really appreciate your vote to help us win for the 10th time.",
  btaBtnLabel: "Click to vote",
  btaBtnLink: "https://britishtravelawards.com/vote/",
  btaLogo:
    "https://uat-author.staysuregroup.com/dam/jcr:88bdc5f6-dae3-4acc-8212-b647eb3d5294/british-travel-awards-2026.png",
  btaLogoAlt: "British Travel Awards",
} as const;

export interface BtaVotingContentProps {
  btaTitle1?: string;
  btaTitle2?: string;
  btaPara1?: string;
  btaPara2?: string;
  btaBtnLabel?: string;
  btaBtnLink?: string;
  btaLogo?: string;
  btaLogoAlt?: string;
  btaEnabled?: boolean;
  variant: "1" | "2";
}

function BtaVotingContent({
  btaTitle1 = DEFAULT_BTA_CONTENT.btaTitle1,
  btaTitle2 = DEFAULT_BTA_CONTENT.btaTitle2,
  btaPara1 = DEFAULT_BTA_CONTENT.btaPara1,
  btaPara2 = DEFAULT_BTA_CONTENT.btaPara2,
  btaBtnLabel = DEFAULT_BTA_CONTENT.btaBtnLabel,
  btaBtnLink = DEFAULT_BTA_CONTENT.btaBtnLink,
  btaLogo = DEFAULT_BTA_CONTENT.btaLogo,
  btaLogoAlt = DEFAULT_BTA_CONTENT.btaLogoAlt,
  btaEnabled = true,
  variant = "1",
}: Readonly<BtaVotingContentProps>) {
  // Resolve the btaLogo to a full URL - memoized to avoid recomputation on every render
  const resolvedLogoSrc = useMemo(
    () => resolveMagnoliaAssetUrl(btaLogo),
    [btaLogo]
  );

  // Memoize the click handler to prevent recreation on every render
  const handleVoteClick = useCallback(() => {
    window.open(btaBtnLink, "_blank", "noopener,noreferrer");
  }, [btaBtnLink]);

  // Variant-based class names - centralized for easier maintenance
  const isVariant2 = variant === "2";
  let sectionClasses = "";
  if (isVariant2) {
    sectionClasses =
      "lg:items-center mt-compact-spacing-s mb-spacing-xl md:mt-spacing-l";
  } else if (APPLICATION === RenewalPage) {
    sectionClasses = "mt-compact-spacing-l md:mt-spacing-xl lg:mt-spacing-l";
  }
  const headerClasses = isVariant2 ? "md:flex-row lg:flex-col" : "md:flex-row";
  const textAlignmentClasses = isVariant2 ? "lg:text-center" : "";

  // If disabled, don't render anything
  if (!btaEnabled) {
    return null;
  }

  return (
    <section
      data-testid="bta-voting-section"
      className={`bg-surface-neutral-option-1 rounded-2xl p-spacing-m md:p-spacing-xl w-full flex flex-col gap-spacing-m md:gap-spacing-l justify-center relative items-start ${sectionClasses}`}
    >
      <div
        className={`flex flex-col ${headerClasses} gap-spacing-m md:gap-spacing-l justify-start md:items-center`}
      >
        <Image
          loader={() => resolvedLogoSrc}
          src={resolvedLogoSrc}
          alt={btaLogoAlt}
          width={120}
          height={120.48}
          loading="lazy"
        />
        <h2 className="font-header-font font-normal text-m md:text-3xl leading-custom-size-compact md:leading-custom-size-xl text-text-title text-start">
          {btaTitle1}
          <br />
          {btaTitle2}
        </h2>
      </div>
      <p
        className={`font-body-font font-medium text-xs md:text-s leading-custom-size-sm md:leading-[23px] text-start ${textAlignmentClasses} text-text-functional`}
      >
        {btaPara1}
      </p>
      <p
        className={`font-body-font font-medium text-xs md:text-s leading-custom-size-sm md:leading-[23px] text-start ${textAlignmentClasses} text-text-functional lg:pr-1.5`}
      >
        {btaPara2}
      </p>
      <LabelButtonWrapper
        type="button"
        label={btaBtnLabel}
        hierarchy="primary"
        semanticColor="Light Background"
        className="!bg-[#FFD050] hover:!bg-[#FFD973] md:max-w-[243px] w-full h-size-xxl !rounded-radius-full leading-custom-size-xs"
        onClick={handleVoteClick}
        data-testid="bta-vote-button"
      />
    </section>
  );
}

export default BtaVotingContent;
